import type { DesignTokens } from "../types/theme";

const WEB_SYSTEM_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * Base tokens for Light mode.
 *
 * Malla identity: light cool slate with a cyan connection accent and violet node secondary.
 * Hex mirror of packages/ui/vars.css (effective source at runtime).
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#eef2f6",
  foreground: "#1b2130",
  surface: "#ffffff",
  surfaceForeground: "#1b2130",
  surfaceSecondary: "#e9eef3",
  surfaceSecondaryForeground: "#1b2130",
  surfaceTertiary: "#dde4ec",
  surfaceTertiaryForeground: "#1b2130",
  overlay: "#ffffff",
  overlayForeground: "#1b2130",
  muted: "#6b7488",
  default: "#e6ebf1",
  defaultForeground: "#1b2130",
  accent: "#0e94a8",
  accentForeground: "#ffffff",
  danger: "#d23a2f",
  dangerForeground: "#ffffff",
  warning: "#c88a1a",
  warningForeground: "#1b2130",
  success: "#12a077",
  successForeground: "#ffffff",
  separator: "#a6b0be",
  glassBackground: "rgba(255, 255, 255, 0.85)",
  glassBorder: "rgba(27, 33, 48, 0.1)",
  glassHighlight: "rgba(27, 33, 48, 0.08)",
  glow0: "rgba(14, 148, 168, 0.13)",
  glow1: "rgba(139, 116, 235, 0.11)",
  glow2: "rgba(18, 160, 119, 0.08)",
  mutedForeground: "#1b2130",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(27, 33, 48, 0.1)",
  overlayShadow: "rgba(27, 33, 48, 0.1)",
  fieldShadow: "rgba(27, 33, 48, 0.1)",
  fieldBorder: "rgba(27, 33, 48, 0.12)",
  fieldPlaceholder: "rgba(27, 33, 48, 0.42)",
  fieldBackground: "rgba(27, 33, 48, 0.03)",
  segment: "#ffffff",
  segmentForeground: "#1b2130",
  link: "#0e94a8",
  focus: "#0e94a8",
  fieldForeground: "#1b2130",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#c4cdd9",
  primary: "#0e94a8",
  primaryForeground: "#ffffff",
  secondary: "#e6ebf1",
  secondaryForeground: "#1b2130",
  backdrop: "rgba(19, 23, 34, 0.5)",
};

/**
 * Base tokens for Dark mode (protagonista).
 *
 * Deep slate charcoal with an electric cyan connection accent. Hex mirror of
 * packages/ui/vars.css (effective source at runtime).
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#131722",
  foreground: "#eef3f5",
  surface: "#1a1f2a",
  surfaceForeground: "#eef3f5",
  surfaceSecondary: "#212836",
  surfaceSecondaryForeground: "#eef3f5",
  surfaceTertiary: "#2a3243",
  surfaceTertiaryForeground: "#eef3f5",
  overlay: "#1e2530",
  overlayForeground: "#eef3f5",
  muted: "#9aa7b8",
  default: "#273040",
  defaultForeground: "#eef3f5",
  accent: "#3ad6d0",
  accentForeground: "#131722",
  danger: "#ef5a50",
  dangerForeground: "#eef3f5",
  warning: "#eab54a",
  warningForeground: "#131722",
  success: "#34d19b",
  successForeground: "#131722",
  separator: "#38424f",
  glassBackground: "rgba(26, 31, 42, 0.985)",
  glassBorder: "rgba(255, 255, 255, 0.12)",
  glassHighlight: "rgba(255, 255, 255, 0.25)",
  glow0: "rgba(58, 214, 208, 0.5)",
  glow1: "rgba(139, 116, 235, 0.42)",
  glow2: "rgba(52, 209, 155, 0.32)",
  mutedForeground: "#eef3f5",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.3)",
  overlayShadow: "rgba(0, 0, 0, 0.3)",
  fieldShadow: "rgba(0, 0, 0, 0.3)",
  fieldBorder: "rgba(255, 255, 255, 0.1)",
  fieldPlaceholder: "rgba(255, 255, 255, 0.42)",
  fieldBackground: "rgba(255, 255, 255, 0.03)",
  segment: "#313b4a",
  segmentForeground: "#eef3f5",
  link: "#3ad6d0",
  focus: "#3ad6d0",
  fieldForeground: "#eef3f5",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#333d4b",
  primary: "#3ad6d0",
  primaryForeground: "#131722",
  secondary: "#273040",
  secondaryForeground: "#eef3f5",
  backdrop: "rgba(0, 0, 0, 0.7)",
};
